import { useCallback, useEffect, useMemo } from 'react';
import { clearAlert, clearAllAlerts, filterClearedAlerts } from '@/utils/clearedAlerts';
import {
  clearPersistedTripAlertRead,
  clearPersistedTripAlertReads,
  getTripAlertReadTimestamps,
  mergePersistedReadIntoNotifications,
  persistTripAlertRead,
} from '@/utils/tripAlertReadPersistence';
import type { TripAlert } from '@/utils/tripAlerts';
import { tripAlertsToNotifications } from './adapters';
import {
  getNotificationsForTrip,
  selectNotificationCount,
  selectNotificationCountForTrip,
  selectNotifications,
  selectNotificationsForTrip,
  useNotificationStore,
} from './store';
import type { Notification, NotificationScope } from './types';

type TripNotificationSyncInput = {
  scopeId?: string;
  alerts: TripAlert[];
  onFilteredAlerts?: (alerts: TripAlert[]) => void;
};

export const filterTripAlerts = (alerts: TripAlert[]): Promise<TripAlert[]> =>
  filterClearedAlerts(alerts);

export const buildNotificationsFromTripAlerts = async (
  alerts: TripAlert[],
): Promise<{ notifications: Notification[]; filteredAlerts: TripAlert[] }> => {
  const filteredAlerts = await filterTripAlerts(alerts);
  const notifications = tripAlertsToNotifications(filteredAlerts, Date.now());
  return { notifications, filteredAlerts };
};

/**
 * Plan tab seeds visa/offline with upsertMany; merge persisted `readAt` + in-session overrides.
 */
export async function hydrateTripAlertNotificationsWithPersistence(
  notifications: Notification[],
): Promise<Notification[]> {
  const readMap = await getTripAlertReadTimestamps();
  const fromDisk = mergePersistedReadIntoNotifications(notifications, readMap);
  const items = useNotificationStore.getState().items;
  return fromDisk.map((n) => {
    const ex = items[n.id];
    if (!ex) return n;
    if (ex.readAt != null && ex.readAt > 0) return { ...n, readAt: ex.readAt };
    if (ex.readAt === null) return { ...n, readAt: null };
    return n;
  });
}

export const refreshTripNotifications = async (input: TripNotificationSyncInput): Promise<Notification[]> => {
  const { notifications, filteredAlerts } = await buildNotificationsFromTripAlerts(input.alerts);
  if (input.onFilteredAlerts) {
    input.onFilteredAlerts(filteredAlerts);
  }
  const hydrated = await hydrateTripAlertNotificationsWithPersistence(notifications);
  useNotificationStore.getState().replaceScope('trip', input.scopeId, hydrated);
  return hydrated;
};

export const dismissNotification = async (id: string): Promise<void> => {
  useNotificationStore.getState().dismiss(id);
  await clearAlert(id);
  await clearPersistedTripAlertRead(id);
};

export const dismissAllNotifications = async (notifications: Notification[]): Promise<void> => {
  const ids = notifications.map((n) => n.id);
  if (ids.length === 0) return;
  useNotificationStore.getState().upsertMany(
    ids.map((id) => ({
      ...useNotificationStore.getState().items[id],
      dismissedAt: Date.now(),
    })) as Notification[],
  );
  await clearAllAlerts(ids);
  await clearPersistedTripAlertReads(ids);
};

export const markNotificationRead = (id: string): void => {
  const before = useNotificationStore.getState().items[id]?.readAt;
  useNotificationStore.getState().markRead(id);
  const after = useNotificationStore.getState().items[id]?.readAt;
  if (after != null && after > 0 && (before == null || before === 0)) {
    void persistTripAlertRead(id, after);
  }
};

export const markNotificationUnread = (id: string): void => {
  useNotificationStore.getState().markUnread(id);
  void clearPersistedTripAlertRead(id);
};

export const dismissNotificationsByIds = async (ids: string[]): Promise<void> => {
  if (!ids.length) return;
  const now = Date.now();
  useNotificationStore.getState().upsertMany(
    ids.map((id) => ({
      ...useNotificationStore.getState().items[id],
      dismissedAt: now,
    })) as Notification[],
  );
  await clearAllAlerts(ids);
  await clearPersistedTripAlertReads(ids);
};

const notificationBelongsToTrip = (notification: Notification | undefined, tripId: string): boolean => {
  if (!notification || notification.scope !== 'trip') return false;
  return notification.scopeId === tripId || (notification.meta?.tripId as string | undefined) === tripId;
};

export const clearNotificationsForTrip = async (tripId: string): Promise<void> => {
  if (!tripId) return;

  const state = useNotificationStore.getState();
  const ids = Object.values(state.items)
    .filter((notification) => notificationBelongsToTrip(notification, tripId))
    .map((notification) => notification.id);

  if (ids.length === 0) return;

  const now = Date.now();
  state.upsertMany(
    ids.map((id) => ({
      ...state.items[id],
      dismissedAt: now,
    })) as Notification[],
  );

  await clearAllAlerts(ids);
  await clearPersistedTripAlertReads(ids);
};

const tripVisaNotificationId = (tripId: string) => `visa-${tripId}`;

/**
 * Trip alerts use `readAt` as the visa checklist "done" state for `kind: 'visa'`.
 */
export function syncVisaChecklistTripNotification(input: {
  tripId: string;
  tripName?: string;
  allItemsComplete: boolean;
  shouldRevertRead: boolean;
}): void {
  const id = tripVisaNotificationId(input.tripId);
  const store = useNotificationStore.getState();
  const existing = store.items[id];

  if (input.allItemsComplete) {
    const base: Notification =
      existing ??
      ({
        id,
        title: 'Visa check',
        body: 'Visa requirements checklist completed.',
        scope: 'trip',
        scopeId: input.tripId,
        kind: 'visa',
        severity: 'warning',
        createdAt: 0,
        source: 'local',
        meta: { tripId: input.tripId, tripName: input.tripName },
        cta: {
          label: 'Check visa requirements',
          action: { type: 'callback', id: 'visa' },
        },
      } as Notification);

    const readAt = Date.now();
    store.upsertMany([
      {
        ...base,
        readAt,
        meta: {
          ...base.meta,
          tripId: input.tripId,
          tripName: input.tripName ?? (base.meta?.tripName as string | undefined),
        },
      },
    ]);
    void persistTripAlertRead(id, readAt);
    return;
  }

  if (input.shouldRevertRead && existing?.readAt) {
    markNotificationUnread(id);
    void clearPersistedTripAlertRead(id);
  }
}

export const markNotificationsReadByIds = (ids: string[]): void => {
  if (!ids.length) return;
  ids.forEach((id) => markNotificationRead(id));
};

export const markAllNotificationsRead = (scope: NotificationScope, scopeId?: string): void => {
  useNotificationStore.getState().markAllRead(scope, scopeId);
};

export const resetNotificationsState = async (): Promise<void> => {
  useNotificationStore.getState().resetStore();
};

export const useNotifications = (scope: NotificationScope, scopeId?: string) =>
  useNotificationStore((state) => selectNotifications(scope, scopeId)(state));

export const useNotificationCount = (scope: NotificationScope, scopeId?: string) =>
  useNotificationStore((state) => selectNotificationCount(scope, scopeId)(state));

export const useTripNotificationSync = (input: TripNotificationSyncInput) => {
  const alertsKey = useMemo(() => {
    if (!input.alerts?.length) return '';
    return input.alerts.map((a) => a.id).sort().join('|');
  }, [input.alerts]);

  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const { notifications, filteredAlerts } = await buildNotificationsFromTripAlerts(input.alerts);
      if (cancelled) return;
      // Avoid churn/loops: only update downstream state when the effective alert IDs changed.
      const filteredKey = filteredAlerts.map((a) => a.id).sort().join('|');
      const bucketKey = `trip:${input.scopeId ?? 'global'}`;
      const currentKey =
        useNotificationStore.getState().buckets?.[bucketKey]?.slice()?.sort()?.join('|') || '';

      if (currentKey !== filteredKey) {
        input.onFilteredAlerts?.(filteredAlerts);
        const hydrated = await hydrateTripAlertNotificationsWithPersistence(notifications);
        if (cancelled) return;
        useNotificationStore.getState().replaceScope('trip', input.scopeId, hydrated);
      }
    };
    sync().catch((error) => console.error('[Notifications] Failed to sync trip alerts', error));

    return () => {
      cancelled = true;
    };
  }, [alertsKey, input.scopeId]);
};

/**
 * Get notifications for a specific trip, filtered from the global 'trip' bucket.
 * Use this when you need trip-specific notifications but want to read from
 * the centralized source of truth populated by the Trips screen.
 *
 * The selector is memoised on `tripId` so the same function identity is reused
 * across re-renders, avoiding unnecessary Zustand subscription churn.
 */
export const useNotificationsForTrip = (tripId: string | undefined) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selector = useCallback(
    (state: ReturnType<typeof useNotificationStore.getState>) =>
      getNotificationsForTrip(state, tripId),
    [tripId],
  );
  return useNotificationStore(selector);
};

/**
 * Get the count of notifications for a specific trip.
 */
export const useNotificationCountForTrip = (tripId: string | undefined) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selector = useCallback(
    (state: ReturnType<typeof useNotificationStore.getState>) =>
      getNotificationsForTrip(state, tripId).filter(
        (n) => n.readAt == null || n.readAt <= 0,
      ).length,
    [tripId],
  );
  return useNotificationStore(selector);
};
